import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  FormControl,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { GoogleGenerativeAI } from '@google/generative-ai';

// Assuming you have access to the Gemini API setup
const genAi = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API);
const model = genAi.getGenerativeModel({ model: 'gemini-2.5-flash-lite' });

const ComplianceTraining = ({ isOpen, onClose, onSubmitAnswers }) => {
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [score, setScore] = useState(null);

  const complianceQuestions = [
    {
      question: '1. What is the policy on accepting gifts from clients?',
      options: [
        'Gifts under $50 are acceptable; report higher value gifts.',
        'All gifts are acceptable.',
        'No gifts are acceptable.',
      ],
      correctAnswer: 'Gifts under $50 are acceptable; report higher value gifts.',
    },
    {
      question: '2. How do I report a colleague misusing company resources?',
      options: [
        'Directly confront the colleague.',
        'Submit a confidential report or address directly.',
        'Ignore the behavior.',
      ],
      correctAnswer: 'Submit a confidential report or address directly.',
    },
    {
      question: '3. Can I make an anonymous harassment report?',
      options: [
        'Yes, using the anonymous reporting tool.',
        'No, reports must include your name.',
        'Only if the HR department allows it.',
      ],
      correctAnswer: 'Yes, using the anonymous reporting tool.',
    },
    {
      question: '4. What are the rules about working hours and breaks?',
      options: [
        '9:00 AM to 6:00 PM, with an hour-long lunch break.',
        'Flexible hours only with permission.',
        'No fixed hours; work anytime.',
      ],
      correctAnswer: '9:00 AM to 6:00 PM, with an hour-long lunch break.',
    },
    {
      question: '5. Can I use company assets for personal work occasionally?',
      options: [
        'It is discouraged but possible with approval.',
        'Yes, with no restrictions.',
        'No, it is strictly prohibited.',
      ],
      correctAnswer: 'It is discouraged but possible with approval.',
    },
    {
      question: '6. What should I do if I see someone accessing confidential files?',
      options: [
        'Report the incident confidentially.',
        'Confront the person accessing the files.',
        'Ignore the situation.',
      ],
      correctAnswer: 'Report the incident confidentially.',
    },
    {
      question: '7. What is the procedure for reporting workplace discrimination?',
      options: [
        'Submit anonymously or directly through the HR portal.',
        'Discuss with the team first.',
        'No specific procedure is provided.',
      ],
      correctAnswer: 'Submit anonymously or directly through the HR portal.',
    },
    {
      question: '8. Can I share sensitive company data with external partners?',
      options: [
        'Only with prior approval from management.',
        'Yes, if you trust the partner.',
        'No, sharing sensitive data is prohibited.',
      ],
      correctAnswer: 'Only with prior approval from management.',
    },
    {
      question: '9. How should I handle a conflict with a colleague?',
      options: [
        'Seek mediation through HR if needed.',
        'Avoid the colleague completely.',
        'Discuss the issue openly in a team meeting.',
      ],
      correctAnswer: 'Seek mediation through HR if needed.',
    },
    {
      question: '10. What is the guideline for participating in social media discussions about the company?',
      options: [
        'Ensure any posts comply with company policies.',
        'Share any opinions freely without restrictions.',
        'Avoid discussing the company on social media.',
      ],
      correctAnswer: 'Ensure any posts comply with company policies.',
    },
    {
      question: '11. What steps should you take if your work equipment is stolen?',
      options: [
        'Report the theft to IT and security immediately.',
        'Replace the equipment yourself.',
        'Wait to see if it turns up.',
      ],
      correctAnswer: 'Report the theft to IT and security immediately.',
    },
    {
      question: '12. How do you address a team member who consistently misses deadlines?',
      options: [
        'Discuss the issue privately and offer support.',
        'Report the person to HR immediately.',
        'Ignore and let the manager handle it.',
      ],
      correctAnswer: 'Discuss the issue privately and offer support.',
    },
    {
      question: '13. What is the company policy on remote work security?',
      options: [
        'Use only company-approved devices and VPNs.',
        'Any device can be used for convenience.',
        'Security measures are optional.',
      ],
      correctAnswer: 'Use only company-approved devices and VPNs.',
    },
  ];

  useEffect(() => {
    if (isOpen) {
      setSelectedAnswers({});
      setScore(null);
    }
  }, [isOpen]);

  const handleAnswerSelection = (questionIndex, answer) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionIndex]: answer,
    }));
  };

  const handleSubmitAnswers = async () => {
    if (Object.keys(selectedAnswers).length === complianceQuestions.length) {
      setLoading(true);
      setError('');
      const answers = Object.values(selectedAnswers);

      try {
        const correctAnswersCount = complianceQuestions.reduce((count, question, index) => {
          return count + (question.correctAnswer === selectedAnswers[index] ? 1 : 0);
        }, 0);
        const totalQuestions = complianceQuestions.length;
        const score = `${correctAnswersCount} out of ${totalQuestions}`;
        setScore(score);
        onSubmitAnswers(score);
      } catch (err) {
        console.error('Error fetching score:', err);
        setError('Error calculating the score. Please try again.');
      } finally {
        setLoading(false);
        setTimeout(() => onClose(), 500); 
      }
    } else {
      alert('Please answer all questions before submitting.');
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="compliance-training-title"
      aria-describedby="compliance-training-description"
    >
      <DialogTitle id="compliance-training-title">Compliance Training</DialogTitle>
      <DialogContent dividers>
        <Typography id="compliance-training-description" variant="body1" sx={{ mb: 2 }}>
          Please answer the following questions as part of your compliance training.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {complianceQuestions.map((question, index) => (
            <FormControl key={index} component="fieldset" sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                {question.question}
              </Typography>
              <RadioGroup
                value={selectedAnswers[index] || ''}
                onChange={(e) => handleAnswerSelection(index, e.target.value)}
              >
                {question.options.map((option, idx) => (
                  <FormControlLabel key={idx} value={option} control={<Radio />} label={option} />
                ))}
              </RadioGroup>
            </FormControl>
          ))}
        </Box>
        {/* {error && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )} */}
        {score && (
          <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
            Your score is: {score}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmitAnswers}
          variant="contained"
          color="inherit"
          disabled={loading || Object.keys(selectedAnswers).length < complianceQuestions.length}
        >
          {loading ? <CircularProgress size={24} /> : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComplianceTraining;
